import { useCallback, useEffect, useState } from 'react'
import { Cloud, Download, Folder, Settings2 } from 'lucide-react'
import { Command } from '@tauri-apps/api/shell'
import { useAuth } from '@/store/auth'
import { isDesktopApp } from '@/services/platform'
import {
  CONTEXT_CHOICES,
  DEFAULTS,
  loadPreferences,
  resetPreferences,
  savePreferences,
  type Preferences,
} from '@/services/preferences'

/** One row: icon, label, explanation, and its control. */
function Row({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-2 py-5">
      <span className="mt-0.5 shrink-0 text-content-muted">{icon}</span>
      <div className="min-w-[14rem] flex-1">
        <p className="font-medium">{title}</p>
        <p className="mt-0.5 text-sm text-content-muted">{description}</p>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  )
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      /*
       * Off is a grey drawn from the muted ink rather than the border colour,
       * which is a hairline tone: at 44x24 it read as an empty outline instead
       * of a switch that is simply off.
       */
      className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
        checked ? 'bg-accent' : 'bg-content-muted/35'
      }`}
    >
      {/*
       * `left-0.5` is load-bearing. Without it the knob is absolute with
       * `left: auto`, so it falls at its static position -- which a button
       * centres -- and the translate then pushed it clear of the track
       * entirely: on light it vanished against the page, on dark it showed as
       * a circle floating past the right edge.
       *
       * The travel is the track less the knob less both insets: 44 - 20 - 4.
       *
       * The knob takes the surface colour rather than a fixed white, so it is
       * white on light and near-black on dark. White on the dark theme's
       * brighter teal sits at about 1.7:1 and the knob all but disappears into
       * the track it is meant to move across.
       */}
      <span
        className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-surface shadow-sm ring-1 ring-black/5 transition-transform dark:ring-white/10 ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  )
}

function formatTokens(tokens: number): string {
  return tokens >= 1024 ? `${Math.round(tokens / 1024)}k` : String(tokens)
}

export function GeneralSettings() {
  const user = useAuth((s) => s.user)
  const logout = useAuth((s) => s.logout)
  const [prefs, setPrefs] = useState<Preferences>(loadPreferences)
  const [modelsDir, setModelsDir] = useState<string | null>(null)

  const update = useCallback((patch: Partial<Preferences>) => {
    setPrefs((current) => {
      const next = { ...current, ...patch }
      savePreferences(next)
      return next
    })
  }, [])

  useEffect(() => {
    // Only the agent knows where weights actually land — an environment
    // variable may have moved it — so this is read rather than assumed.
    if (!isDesktopApp) return
    void Command.sidecar('binaries/nevtan-agent', ['whoami'])
      .execute()
      .then((output) => {
        if (output.code !== 0) return
        const reported = JSON.parse(output.stdout) as { modelsDir?: string }
        if (reported.modelsDir) setModelsDir(reported.modelsDir)
      })
      .catch(() => undefined)
  }, [])

  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border pb-6">
        <div className="min-w-[12rem] flex-1">
          <p className="font-medium">NevTan account</p>
          <p className="mt-0.5 text-sm text-content-muted">
            {user ? user.email : 'Not connected'}
          </p>
        </div>
        {user && (
          <button
            type="button"
            onClick={logout}
            className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-surface-sunken"
          >
            Sign out
          </button>
        )}
      </div>

      <div className="divide-y divide-border">
        <Row
          icon={<Cloud size={18} />}
          title="Cloud models"
          description="Offer hosted models alongside the ones on this machine."
        >
          <Toggle
            checked={prefs.cloudModels}
            onChange={(next) => update({ cloudModels: next })}
            label="Cloud models"
          />
        </Row>

        <Row
          icon={<Download size={18} />}
          title="Check for model updates"
          description="Notice when a model you installed has been rebuilt by its publisher."
        >
          <Toggle
            checked={prefs.checkModelUpdates}
            onChange={(next) => update({ checkModelUpdates: next })}
            label="Check for model updates"
          />
        </Row>

        {isDesktopApp && (
          <Row
            icon={<Folder size={18} />}
            title="Model location"
            description="Where weights are stored on this machine."
          >
            <code className="block max-w-[22rem] truncate rounded bg-surface-sunken px-2 py-1 text-xs">
              {modelsDir ?? 'Reading…'}
            </code>
          </Row>
        )}

        <Row
          icon={<Settings2 size={18} />}
          title="Context length"
          description="How much of a conversation a local model is given. Larger reserves more memory, whether or not the conversation fills it."
        >
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={CONTEXT_CHOICES.length - 1}
              step={1}
              value={Math.max(0, CONTEXT_CHOICES.indexOf(prefs.contextTokens as never))}
              onChange={(e) =>
                update({ contextTokens: CONTEXT_CHOICES[Number(e.target.value)] })
              }
              aria-label="Context length"
              className="w-48 accent-accent"
            />
            <span className="w-10 text-right text-sm tabular-nums text-content-muted">
              {formatTokens(prefs.contextTokens)}
            </span>
          </div>
        </Row>
      </div>

      <div className="flex justify-end pt-6">
        <button
          type="button"
          onClick={() => setPrefs(resetPreferences())}
          disabled={JSON.stringify(prefs) === JSON.stringify(DEFAULTS)}
          className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-surface-sunken disabled:opacity-40"
        >
          Reset to defaults
        </button>
      </div>
    </div>
  )
}
